import { useState } from 'react'
import type { InvoiceSortBy, SortOrder } from '@/types'
import { SortableHead } from '@/components/sortable-head'
import { PERIOD_PRESETS, periodRange, type PeriodPreset } from '@/lib/period-presets'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowUpDownIcon, EllipsisVerticalIcon } from 'lucide-react'
import {
	Badge,
	Button,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	FinancesIcon,
	IconButton,
	MetricCard,
	MoneyReceiveIcon,
	SaveMoneyIcon,
	PanelFilters,
	SearchInput,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import {
	useDeleteInvoiceMutation,
	useGetFinanceMetricsQuery,
	useListContactsQuery,
	useListFinanceInvoicesQuery,
	useListPropertiesQuery,
	useUpdateInvoiceMutation,
} from '@/api'
import { CreateInvoiceDialog } from '@/components/create-invoice-dialog'
import { InvoiceDetailsDialog } from '@/components/invoice-details-dialog'
import { PanelDown } from '@/components/panel-down'
import { DEFAULT_LOCALE, INVOICE_STATUS_KEYS, INVOICE_STATUS_VARIANTS } from '@/types'
import type { Invoice, InvoiceStatus, InvoiceType } from '@/types'
import { useCan } from '@/lib/access'

const INVOICE_TYPES: InvoiceType[] = ['RENT', 'DEPOSIT', 'UTILITIES', 'PENALTY', 'ADDITIONAL', 'ONE_TIME']

export const Route = createFileRoute('/_app/finances/invoices/')({
	component: FinancesInvoicesPage,
})

const PAGE_SIZE = 10

function formatDate(iso: string) {
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatAmount(n: number, currency: string) {
	const sign = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₽'
	return `${n.toLocaleString(DEFAULT_LOCALE)} ${sign}`
}

function formatChange(n: number): { text: string; trend: 'up' | 'down' } {
	const sign = n >= 0 ? '+' : ''
	return { text: `${sign} ${Math.abs(n)}%`, trend: n >= 0 ? 'up' : 'down' }
}

function FinancesInvoicesPage() {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const canCreate = useCan('create', 'Invoice')
	const [search, setSearch] = useState('')
	const [page, setPage] = useState(1)
	const [statusFilter, setStatusFilter] = useState('')
	const [typeFilter, setTypeFilter] = useState('')
	const [propertyFilter, setPropertyFilter] = useState('')
	const [tenantFilter, setTenantFilter] = useState('')
	// Server-side, both of them: the list is paginated by the server, so a
	// browser-side sort would reorder only the page on screen.
	const [period, setPeriod] = useState<PeriodPreset>('all')
	const [sortBy, setSortBy] = useState<InvoiceSortBy | undefined>()
	const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
	const [createOpen, setCreateOpen] = useState(false)
	const [viewInvoice, setViewInvoice] = useState<Invoice | null>(null)

	const { data: metrics } = useGetFinanceMetricsQuery()
	const { data: propertiesData } = useListPropertiesQuery({ limit: 200 })
	const { data: contactsData } = useListContactsQuery({ type: 'TENANT', limit: 200 })
	const { data: invoicesData } = useListFinanceInvoicesQuery({
		status:     (statusFilter   || undefined) as InvoiceStatus | undefined,
		type:       (typeFilter     || undefined) as InvoiceType | undefined,
		propertyId: propertyFilter  || undefined,
		tenantId:   tenantFilter    || undefined,
		search:     search          || undefined,
		...periodRange(period),
		sortBy,
		sortOrder: sortBy ? sortOrder : undefined,
		page,
		limit: PAGE_SIZE,
	})

	function toggleSort(field: InvoiceSortBy) {
		if (sortBy === field) {
			setSortOrder((d) => (d === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortBy(field)
			setSortOrder('asc')
		}
		setPage(1)
	}
	const [updateInvoice] = useUpdateInvoiceMutation()
	const [deleteInvoice] = useDeleteInvoiceMutation()

	const properties = propertiesData?.properties ?? []
	const tenants = contactsData?.items ?? []

	const invoices = invoicesData?.invoices ?? []
	const total = invoicesData?.total ?? 0
	const currency = metrics?.currency ?? 'RUB'

	const accruals = formatChange(Number(metrics?.accrualsChange ?? 0))
	const income = formatChange(Number(metrics?.incomeChange ?? 0))
	const debt = formatChange(Number(metrics?.debtChange ?? 0))

	return (
		<div className="flex h-full flex-col">
			{/* Metric cards */}
			<div className="grid grid-cols-3 gap-4 px-4 pt-4">
				<MetricCard
					icon={<FinancesIcon size={20} />}
					value={metrics ? formatAmount(Number(metrics.accruals), currency) : '—'}
					label={t('finances.metrics.accruals')}
					change={metrics ? accruals.text : undefined}
					trend={accruals.trend}
				/>
				<MetricCard
					icon={<MoneyReceiveIcon size={20} />}
					value={metrics ? formatAmount(Number(metrics.income), currency) : '—'}
					label={t('finances.metrics.income')}
					change={metrics ? income.text : undefined}
					trend={income.trend}
				/>
				<MetricCard
					icon={<SaveMoneyIcon size={20} />}
					value={metrics ? formatAmount(Number(metrics.debt), currency) : '—'}
					label={t('finances.metrics.debt')}
					change={metrics ? debt.text : undefined}
					trend={debt.trend}
					variant="danger"
				/>
			</div>

			{/* Filters */}
			<PanelFilters
				filters={
					<>
						<Select
							value={typeFilter || 'all'}
							onValueChange={(v) => { setTypeFilter(v === 'all' ? '' : v); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('finances.filters.type')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('finances.filters.allTypes')}</SelectItem>
								{INVOICE_TYPES.map((tp) => (
									<SelectItem key={tp} value={tp}>{t(`finances.types.${tp}`)}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={period}
							onValueChange={(v) => { setPeriod(v as PeriodPreset); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('finances.filters.period')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('finances.filters.allPeriods')}</SelectItem>
								{PERIOD_PRESETS.map((preset) => (
									<SelectItem key={preset} value={preset}>
										{t(`finances.filters.periods.${preset}`)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={propertyFilter || 'all'}
							onValueChange={(v) => { setPropertyFilter(v === 'all' ? '' : v); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('finances.filters.rental')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('finances.filters.allRentals')}</SelectItem>
								{properties.map((p) => (
									<SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={tenantFilter || 'all'}
							onValueChange={(v) => { setTenantFilter(v === 'all' ? '' : v); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('finances.filters.tenant')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('finances.filters.allTenants')}</SelectItem>
								{tenants.map((c) => (
									<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={statusFilter || 'all'}
							onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('finances.filters.status')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('finances.filters.status')}</SelectItem>
								<SelectItem value="PAID">{t('finances.statuses.paid')}</SelectItem>
								<SelectItem value="PENDING">{t('finances.statuses.pending')}</SelectItem>
								<SelectItem value="OVERDUE">{t('finances.statuses.overdue')}</SelectItem>
								<SelectItem value="CANCELLED">{t('finances.statuses.cancelled')}</SelectItem>
							</SelectContent>
						</Select>
					</>
				}
				actions={
					<>
						<SearchInput
							placeholder={t('finances.search')}
							value={search}
							className='w-[200px]'
							onChange={(e) => { setSearch(e.target.value); setPage(1) }}
						/>
						{canCreate && (
							<Button size="sm" onClick={() => setCreateOpen(true)}>{t('finances.createInvoice')}</Button>
						)}
					</>
				}
			/>

			{/* Table */}
			{invoices.length === 0 ? (
				<div className="flex flex-1 items-center justify-center">
					<p className="text-sm text-(--color-muted-foreground)">{t('finances.empty')}</p>
				</div>
			) : (
				<>
					<div className="flex min-h-0 flex-1 flex-col overflow-auto px-4">
						<div className="overflow-hidden rounded-lg border border-(--color-border)">
							<Table>
								<TableHeader>
									<TableRow className="hover:bg-transparent">
										<TableHead>
											<Button
												variant='ghost'
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('finances.table.invoice')}
											</Button>
										</TableHead>
										<SortableHead
											field="PERIOD"
											activeField={sortBy}
											order={sortOrder}
											onSort={toggleSort}
											className="w-[140px]"
										>
											{t('finances.table.period')}
										</SortableHead>
										{/* The object column: an invoice belongs to a rental,
										    and a rental to a property, so this orders by the
										    property address. */}
										<SortableHead
											field="PROPERTY"
											activeField={sortBy}
											order={sortOrder}
											onSort={toggleSort}
										>
											{t('finances.table.rental')}
										</SortableHead>
										<TableHead className="w-[120px]">
											<Button
												variant='ghost'
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('finances.table.tenant')}
											</Button>
										</TableHead>
										<SortableHead
											field="AMOUNT"
											activeField={sortBy}
											order={sortOrder}
											onSort={toggleSort}
											className="w-[120px]"
										>
											{t('finances.table.amount')}
										</SortableHead>
										<TableHead className="w-[120px] font-[500] text-(--color-muted-foreground)">
											{t('finances.table.status')}
										</TableHead>
										<TableHead className="w-[48px]" />
									</TableRow>
								</TableHeader>
								<TableBody>
									{invoices.map((inv) => (
										<TableRow key={inv.id} className="h-[72px] cursor-pointer" onClick={() => setViewInvoice(inv)}>
											<TableCell className='px-4'>
												<div className="flex flex-col gap-0.5">
													<span className="text-sm font-[500] text-(--color-foreground)">{inv.title}</span>
													<span className="text-sm text-(--color-muted-foreground)">{formatDate(inv.date ?? inv.createdAt)}</span>
												</div>
											</TableCell>
											<TableCell className="w-[140px] px-4 text-sm text-(--color-foreground)">
												<div>
													{formatDate(inv.periodStart)} -<br />{formatDate(inv.periodEnd)}
												</div>
											</TableCell>
											<TableCell className='px-4' onClick={(e) => e.stopPropagation()}>
												<div className="flex flex-col gap-0.5">
													<span
														className="text-sm font-[500] text-(--color-info) truncate cursor-pointer underline-offset-2 hover:underline"
														onClick={() => navigate({ to: '/rent/$rentalId', params: { rentalId: inv.rentalId } })}
													>
														{inv.propertyTitle ?? '—'}
													</span>
													<span className="text-sm text-(--color-muted-foreground)">
														{inv.propertyDates ?? `${formatDate(inv.periodStart ?? '')} - ${formatDate(inv.periodEnd ?? '')}`}
													</span>
												</div>
											</TableCell>
											<TableCell className="w-[120px] px-4 text-sm text-(--color-foreground)">
												{inv.tenantName ?? '—'}
											</TableCell>
											<TableCell className="w-[120px] px-4 text-sm text-(--color-foreground)">
												{formatAmount(Number(inv.amount), inv.currency)}
											</TableCell>
											<TableCell className="w-[120px] px-4">
												<Badge variant={INVOICE_STATUS_VARIANTS[inv.status]} size="sm">
													{t(INVOICE_STATUS_KEYS[inv.status])}
												</Badge>
											</TableCell>
											<TableCell className="w-[48px] px-4" onClick={(e) => e.stopPropagation()}>
												<DropdownMenu>
													<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('common.actions')} />}>
														<EllipsisVerticalIcon className="size-4" />
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end" className="min-w-48">
													<DropdownMenuItem onClick={() => setViewInvoice(inv)}>
															{t('finances.actions.view')}
														</DropdownMenuItem>
														<DropdownMenuItem onClick={() => void updateInvoice({ id: inv.id, body: { status: 'PENDING' } })}>
															{t('finances.actions.send')}
														</DropdownMenuItem>
														<DropdownMenuItem onClick={() => void updateInvoice({ id: inv.id, body: { status: 'PAID', paidAt: new Date().toISOString() } })}>
															{t('finances.actions.markPaid')}
														</DropdownMenuItem>
														<DropdownMenuItem onClick={() => void updateInvoice({ id: inv.id, body: { status: 'ADJUSTED' } })}>
															{t('finances.actions.adjust')}
														</DropdownMenuItem>
														<DropdownMenuItem onClick={() => void updateInvoice({ id: inv.id, body: { status: 'CANCELLED' } })}>
															{t('finances.actions.cancel')}
														</DropdownMenuItem>
														<DropdownMenuItem onClick={() => void deleteInvoice(inv.id)}>
															{t('common.delete')}
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</div>
					<PanelDown total={total} page={page} onPageChange={setPage} limit={PAGE_SIZE} />
				</>
			)}
			<CreateInvoiceDialog open={createOpen} onOpenChange={setCreateOpen} />
		<InvoiceDetailsDialog open={!!viewInvoice} onOpenChange={(v) => { if (!v) setViewInvoice(null) }} invoice={viewInvoice} />
		</div>
	)
}
